import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  BarChart3, 
  TrendingUp,
  TrendingDown,
  Home,
  ClipboardList,
  MessageCircle,
  Download,
  Calendar,
  Target,
  CheckCircle,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Landmark,
  MapPin,
  UserCheck,
  Network,
  Sparkles,
  Eye,
  Share2,
  Bookmark,
  Phone,
  Trophy,
  Flame,
  Receipt,
  Wallet,
  PiggyBank,
  DollarSign,
  Loader,
  X,
  ChevronDown,
  FileSpreadsheet
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { formatPrice } from '../utils/helpers';
import statisticsService from '../services/statisticsService';
import PermissionGate from '../components/PermissionGate';
import './StatisticsPage.css';

const StatisticsPage = () => {
  useTranslation();
  const [dateRange, setDateRange] = useState('month');
  const [activeMetric, setActiveMetric] = useState('offers');
  const [loading, setLoading] = useState(true);
  
  // Separate state for each endpoint
  const [kpisData, setKpisData] = useState(null);
  const [offersStats, setOffersStats] = useState(null);
  const [requestsStats, setRequestsStats] = useState(null);
  const [matchingStats, setMatchingStatsData] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [financialStats, setFinancialStats] = useState(null);
  const [networkStats, setNetworkStats] = useState(null);
  
  // Export states
  const [showExportModal, setShowExportModal] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const exportDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch all statistics in parallel
  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch each endpoint separately to identify issues
      let kpis = null, offers = null, requests = null, matching = null, performance = [], financial = null, network = null;
      
      try {
        kpis = await statisticsService.getKPIs(dateRange);
        console.log('KPIs loaded:', kpis);
      } catch (e) {
        console.error('KPIs error:', e);
      }
      
      try {
        offers = await statisticsService.getOffersStats();
        console.log('Offers loaded:', offers);
      } catch (e) {
        console.error('Offers error:', e);
      }
      
      try {
        requests = await statisticsService.getRequestsStats();
        console.log('Requests loaded:', requests);
      } catch (e) {
        console.error('Requests error:', e);
      }
      
      try {
        matching = await statisticsService.getMatchingStats();
        console.log('Matching loaded:', matching);
      } catch (e) {
        console.error('Matching error:', e);
      }
      
      try {
        performance = await statisticsService.getPerformanceStats(4);
        console.log('Performance loaded:', performance);
      } catch (e) {
        console.error('Performance error:', e);
      }
      
      try {
        financial = await statisticsService.getFinancialStats(dateRange);
        console.log('Financial loaded:', financial);
      } catch (e) {
        console.error('Financial error:', e);
      }
      
      try {
        network = await statisticsService.getNetworkStats();
        console.log('Network loaded:', network);
      } catch (e) {
        console.error('Network error:', e);
      }
      
      console.log('All Statistics Data:', { kpis, offers, requests, matching, performance, financial, network });
      
      setKpisData(kpis);
      setOffersStats(offers);
      setRequestsStats(requests);
      setMatchingStatsData(matching);
      setPerformanceData(performance || []);
      setFinancialStats(financial);
      setNetworkStats(network);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchStatistics();
  }, [fetchStatistics]);

  // Debug: Log state values after they're set
  useEffect(() => {
    console.log('State Updated - kpisData:', kpisData);
    console.log('State Updated - offersStats:', offersStats);
    console.log('State Updated - requestsStats:', requestsStats);
    console.log('State Updated - financialStats:', financialStats);
    console.log('State Updated - networkStats:', networkStats);
  }, [kpisData, offersStats, requestsStats, financialStats, networkStats]);

  // Export options
  const exportOptions = [
    { id: 'overview', label: 'نظرة عامة', icon: BarChart3 },
    { id: 'offers', label: 'تقرير العروض', icon: Home },
    { id: 'requests', label: 'تقرير الطلبات', icon: ClipboardList },
    { id: 'financial', label: 'التقرير المالي', icon: Receipt }
  ];

  const handleExportClick = (type) => {
    setShowExportDropdown(false);
    if (type) {
      statisticsService.exportReport(type, exportDateRange);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportWithDates = (type) => {
    statisticsService.exportReport(type, exportDateRange);
    setShowExportModal(false);
  };

  // Key Performance Indicators - From API
  const kpis = useMemo(() => ({
    offers: {
      value: kpisData?.offers?.value || 0,
      change: kpisData?.offers?.change || 0,
      target: kpisData?.offers?.target || 10,
      label: 'عروضي',
      icon: Home,
      color: '#10b981'
    },
    requests: {
      value: kpisData?.requests?.value || 0,
      change: kpisData?.requests?.change || 0,
      target: kpisData?.requests?.target || 10,
      label: 'طلبات العملاء',
      icon: ClipboardList,
      color: '#3b82f6'
    },
    matches: {
      value: kpisData?.matches?.value || 0,
      change: kpisData?.matches?.change || 0,
      target: kpisData?.matches?.target || 5,
      label: 'تطابقات',
      icon: Sparkles,
      color: '#8b5cf6'
    },
    following: {
      value: kpisData?.following?.value || 0,
      change: kpisData?.following?.change || 0,
      target: kpisData?.following?.target || 20,
      label: 'متابَعين',
      icon: UserCheck,
      color: '#f59e0b'
    }
  }), [kpisData]);

  // Overview Stats - From API
  const stats = useMemo(() => ({
    totalOffers: { value: offersStats?.total || 0, change: kpisData?.offers?.change || 0, period: 'عروضي' },
    availableOffers: { value: offersStats?.available || 0, change: 0, period: 'متاح' },
    totalRequests: { value: requestsStats?.total || 0, change: kpisData?.requests?.change || 0, period: 'طلب' },
    newRequests: { value: requestsStats?.new || 0, change: 0, period: 'جديد' },
    networkOffers: { value: 0, change: 0, period: 'من الشبكة' },
    following: { value: networkStats?.following || 0, change: kpisData?.following?.change || 0, period: 'متابَع' },
    conversations: { value: networkStats?.conversations || 0, change: 0, period: 'محادثة' },
    excellentMatches: { value: matchingStats?.excellent || 0, change: 0, period: 'تطابق ممتاز' }
  }), [offersStats, requestsStats, networkStats, matchingStats, kpisData]);

  // Funnel data from API - with colors and percentage conversion
  const funnelData = useMemo(() => {
    const colors = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];
    return (requestsStats?.funnelData || []).map((item, index) => ({
      ...item,
      percentage: parseFloat(item.percentage) || 0,
      color: colors[index % colors.length]
    }));
  }, [requestsStats]);

  // Property Performance from API
  const propertyPerformance = useMemo(() => {
    const typeMap = {
      villa: { label: 'فلل', icon: Home, color: '#1a5f4a' },
      apartment: { label: 'شقق', icon: Building2, color: '#3b82f6' },
      land: { label: 'أراضي', icon: Landmark, color: '#f59e0b' }
    };
    
    return (offersStats?.propertyPerformance || []).map(p => ({
      type: typeMap[p.type]?.label || p.type,
      offers: p.count || 0,
      requests: p.requests || 0,
      matches: p.matches || 0,
      icon: typeMap[p.type]?.icon || Building2,
      color: typeMap[p.type]?.color || '#6c757d'
    }));
  }, [offersStats]);

  // City Performance from API
  const cityPerformance = useMemo(() => 
    (offersStats?.cityPerformance || []).map(c => ({
      city: c.city,
      offers: c.count || 0,
      requests: c.requests || 0,
      matches: c.matches || 0,
      growth: c.growth || 0
    })),
    [offersStats]
  );

  // Top Offers from API
  const topOffers = useMemo(() => 
    offersStats?.topOffers || [], 
    [offersStats]
  );

  // Matching Stats from API
  const matchingStatsFormatted = useMemo(() => ({
    excellent: { count: matchingStats?.excellent || 0, label: 'ممتاز (90%+)' },
    good: { count: matchingStats?.good || 0, label: 'جيد (75-89%)' },
    fair: { count: matchingStats?.fair || 0, label: 'مقبول (50-74%)' }
  }), [matchingStats]);

  // Financial Stats from API
  const financialStatsFormatted = useMemo(() => 
    financialStats || {
      totalIncome: 0,
      totalExpenses: 0,
      netProfit: 0,
      pendingIncome: 0,
      thisMonthIncome: 0,
      paidInvoices: 0,
      pendingInvoices: 0
    }, 
    [financialStats]
  );

  // Best Performing Offers - sorted by engagement from topOffers
  const bestPerformingOffers = useMemo(() => 
    [...topOffers].sort((a, b) => 
      (b.views + b.shares * 5 + b.inquiries * 10) - (a.views + a.shares * 5 + a.inquiries * 10)
    ),
    [topOffers]
  );

  // Financial data for chart - placeholder until API provides monthly breakdown
  const financialData = useMemo(() => {
    if (!performanceData || performanceData.length === 0) return [];
    // Distribute total income/expenses across months proportionally to activity
    const totalActivity = performanceData.reduce((sum, item) => sum + (item.offers || 0) + (item.requests || 0), 0) || 1;
    return performanceData.map(item => {
      const monthActivity = (item.offers || 0) + (item.requests || 0);
      const monthRatio = monthActivity / totalActivity;
      return {
        month: item.month,
        income: Math.round((financialStatsFormatted.totalIncome || 0) * monthRatio),
        expenses: Math.round((financialStatsFormatted.totalExpenses || 0) * monthRatio)
      };
    });
  }, [performanceData, financialStatsFormatted]);

  const totalOffers = useMemo(() => 
    propertyPerformance.reduce((sum, p) => sum + (p.offers || 0), 0), 
    [propertyPerformance]);

  if (loading) {
    return (
      <div className="statistics-page">
        <div className="loading-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Loader className="spin" size={40} />
        </div>
      </div>
    );
  }

  return (
    <PermissionGate feature="PAGE_STATISTICS" type="page">
    <div className="statistics-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-title">
          <BarChart3 size={28} />
          <div>
            <h1>لوحة الإحصائيات</h1>
            <p>نظرة شاملة على أدائك في التسويق العقاري</p>
          </div>
        </div>
        <div className="header-actions">
          <div className="date-filter-wrapper">
            <Calendar size={18} />
            <select 
              className="date-filter"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="week">آخر 7 أيام</option>
              <option value="month">آخر 30 يوم</option>
              <option value="quarter">آخر 3 أشهر</option>
              <option value="year">السنة الحالية</option>
            </select>
          </div>
          
          {/* Export Dropdown */}
          <div className="export-dropdown-container" ref={exportDropdownRef}>
            <button 
              className="btn btn-primary"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              <Download size={18} />
              تصدير التقرير
              <ChevronDown size={16} />
            </button>
            
            {showExportDropdown && (
              <div className="export-dropdown">
                <div className="export-dropdown-header">
                  <span>اختر نوع التقرير</span>
                </div>
                {exportOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      className="export-dropdown-item"
                      onClick={() => handleExportClick(option.id)}
                    >
                      <Icon size={18} />
                      <span>{option.label}</span>
                      <FileSpreadsheet size={16} className="file-icon" />
                    </button>
                  );
                })}
                <div className="export-dropdown-divider"></div>
                <button
                  className="export-dropdown-item custom-range"
                  onClick={() => handleExportClick(null)}
                >
                  <Calendar size={18} />
                  <span>تحديد نطاق تاريخ مخصص</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="export-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <Download size={22} />
                تصدير التقارير
              </h3>
              <button className="close-btn" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="date-range-section">
                <h4>
                  <Calendar size={18} />
                  حدد نطاق التاريخ
                </h4>
                <div className="date-inputs">
                  <div className="date-input-group">
                    <label>من تاريخ</label>
                    <input
                      type="date"
                      value={exportDateRange.startDate}
                      onChange={(e) => setExportDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    />
                  </div>
                  <div className="date-input-group">
                    <label>إلى تاريخ</label>
                    <input
                      type="date"
                      value={exportDateRange.endDate}
                      onChange={(e) => setExportDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="quick-ranges">
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.setDate(now.getDate() - 7)).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>آخر أسبوع</button>
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>هذا الشهر</button>
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>آخر 3 أشهر</button>
                  <button onClick={() => {
                    const now = new Date();
                    setExportDateRange({
                      startDate: new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0],
                      endDate: new Date().toISOString().split('T')[0]
                    });
                  }}>هذا العام</button>
                </div>
              </div>
              
              <div className="export-types-section">
                <h4>
                  <FileSpreadsheet size={18} />
                  اختر التقرير للتصدير
                </h4>
                <div className="export-types-grid">
                  {exportOptions.map((option) => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        className="export-type-card"
                        onClick={() => handleExportWithDates(option.id)}
                      >
                        <div className="export-type-icon">
                          <Icon size={24} />
                        </div>
                        <span>{option.label}</span>
                        <Download size={16} className="download-icon" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="kpi-grid">
        {Object.entries(kpis).map(([key, kpi]) => {
          const Icon = kpi.icon;
          const progress = (kpi.value / kpi.target) * 100;
          return (
            <div 
              key={key} 
              className={`kpi-card ${activeMetric === key ? 'active' : ''}`}
              onClick={() => setActiveMetric(key)}
            >
              <div className="kpi-header">
                <div className="kpi-icon" style={{ background: `${kpi.color}20`, color: kpi.color }}>
                  <Icon size={24} />
                </div>
                <div className={`kpi-change ${kpi.change >= 0 ? 'positive' : 'negative'}`}>
                  {kpi.change >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                  {Math.abs(kpi.change)}%
                </div>
              </div>
              <div className="kpi-content">
                <span className="kpi-value">
                  {kpi.value}{kpi.suffix || ''}
                </span>
                <span className="kpi-label">{kpi.label}</span>
              </div>
              <div className="kpi-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${Math.min(progress, 100)}%`, background: kpi.color }}
                  />
                </div>
                <span className="progress-text">
                  {progress.toFixed(0)}% من الهدف
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick Stats Row */}
      <div className="quick-stats">
        <div className="quick-stat">
          <div className="stat-icon-mini offers">
            <Home size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{stats.totalOffers.value}</span>
            <span className="stat-label">{stats.totalOffers.period}</span>
          </div>
          <span className={`stat-badge ${stats.totalOffers.change >= 0 ? 'up' : 'down'}`}>
            {stats.totalOffers.change >= 0 ? '+' : ''}{stats.totalOffers.change}%
          </span>
        </div>
        <div className="quick-stat">
          <div className="stat-icon-mini requests">
            <ClipboardList size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{stats.totalRequests.value}</span>
            <span className="stat-label">{stats.totalRequests.period}</span>
          </div>
          <span className={`stat-badge ${stats.totalRequests.change >= 0 ? 'up' : 'down'}`}>
            {stats.totalRequests.change >= 0 ? '+' : ''}{stats.totalRequests.change}%
          </span>
        </div>
        <div className="quick-stat">
          <div className="stat-icon-mini views">
            <Sparkles size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{matchingStatsFormatted.excellent.count + matchingStatsFormatted.good.count + matchingStatsFormatted.fair.count}</span>
            <span className="stat-label">تطابقات</span>
          </div>
          <span className="stat-badge up">+{kpis.matches.change}%</span>
        </div>
        <div className="quick-stat">
          <div className="stat-icon-mini contacts">
            <MessageCircle size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{stats.conversations.value}</span>
            <span className="stat-label">{stats.conversations.period}</span>
          </div>
          <span className={`stat-badge ${stats.conversations.change >= 0 ? 'up' : 'down'}`}>
            {stats.conversations.change >= 0 ? '+' : ''}{stats.conversations.change}%
          </span>
        </div>
        <div className="quick-stat">
          <div className="stat-icon-mini visitors">
            <Network size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{stats.networkOffers.value}</span>
            <span className="stat-label">{stats.networkOffers.period}</span>
          </div>
          <span className={`stat-badge ${stats.networkOffers.change >= 0 ? 'up' : 'down'}`}>
            {stats.networkOffers.change >= 0 ? '+' : ''}{stats.networkOffers.change}%
          </span>
        </div>
        <div className="quick-stat">
          <div className="stat-icon-mini calls">
            <UserCheck size={20} />
          </div>
          <div className="stat-details">
            <span className="stat-number">{stats.following.value}</span>
            <span className="stat-label">{stats.following.period}</span>
          </div>
          <span className={`stat-badge ${stats.following.change >= 0 ? 'up' : 'down'}`}>
            {stats.following.change >= 0 ? '+' : ''}{stats.following.change}%
          </span>
        </div>
      </div>

      {/* Main Charts Row */}
      <div className="charts-row">
        <div className="chart-card main-chart">
          <div className="chart-header">
            <div>
              <h3>نمو النشاط</h3>
              <p className="chart-subtitle">تتبع العروض والطلبات شهرياً</p>
            </div>
            <div className="chart-tabs">
              <button className={activeMetric === 'offers' ? 'active' : ''} onClick={() => setActiveMetric('offers')}>
                العروض
              </button>
              <button className={activeMetric === 'requests' ? 'active' : ''} onClick={() => setActiveMetric('requests')}>
                الطلبات
              </button>
              <button className={activeMetric === 'following' ? 'active' : ''} onClick={() => setActiveMetric('following')}>
                المتابعين
              </button>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="colorOffers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorFollowing" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis dataKey="month" stroke="var(--text-secondary)" fontSize={12} />
                <YAxis stroke="var(--text-secondary)" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'var(--surface-color)', 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '12px',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                  formatter={(value, name) => {
                    if (name === 'offers') return [value, 'العروض'];
                    if (name === 'requests') return [value, 'الطلبات'];
                    if (name === 'following') return [value, 'المتابعين'];
                    return [value, name];
                  }}
                />
                {activeMetric === 'offers' && (
                  <Area 
                    type="monotone" 
                    dataKey="offers" 
                    stroke="#10b981" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorOffers)" 
                  />
                )}
                {activeMetric === 'requests' && (
                  <Area 
                    type="monotone" 
                    dataKey="requests" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorRequests)" 
                  />
                )}
                {activeMetric === 'following' && (
                  <Area 
                    type="monotone" 
                    dataKey="following" 
                    stroke="#8b5cf6" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorFollowing)" 
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card funnel-chart">
          <div className="chart-header">
            <div>
              <h3>حالة الطلبات</h3>
              <p className="chart-subtitle">توزيع الطلبات حسب الحالة</p>
            </div>
          </div>
          <div className="funnel-container">
            {funnelData.map((stage, index) => (
              <div key={index} className="funnel-stage">
                <div className="funnel-bar-container">
                  <div 
                    className="funnel-bar"
                    style={{ 
                      width: `${stage.percentage}%`,
                      background: stage.color
                    }}
                  />
                </div>
                <div className="funnel-info">
                  <span className="funnel-label">{stage.stage}</span>
                  <span className="funnel-value">{stage.value}</span>
                  <span className="funnel-percent">{stage.percentage.toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
          <div className="funnel-summary">
            <div className="summary-item">
              <Sparkles size={18} />
              <span>إجمالي الطلبات: <strong>{requestsStats?.total || 0}</strong></span>
            </div>
          </div>
        </div>
      </div>

      {/* Property Performance Section */}
      <div className="section-header">
        <h2>توزيع أنواع العقارات</h2>
        <p>إحصائيات حسب نوع العقار</p>
      </div>
      <div className="property-performance-grid">
        {propertyPerformance.map((property, index) => {
          const Icon = property.icon;
          const offerPercent = (property.offers / totalOffers) * 100;
          return (
            <div key={index} className="property-performance-card">
              <div className="property-header">
                <div className="property-icon" style={{ background: `${property.color}15`, color: property.color }}>
                  <Icon size={24} />
                </div>
                <div className="property-title">
                  <h4>{property.type}</h4>
                  <span>{property.offers} عرض</span>
                </div>
              </div>
              <div className="property-metrics">
                <div className="metric">
                  <ClipboardList size={16} />
                  <span className="metric-value">{property.requests}</span>
                  <span className="metric-label">طلب</span>
                </div>
                <div className="metric">
                  <Sparkles size={16} />
                  <span className="metric-value">{property.matches}</span>
                  <span className="metric-label">تطابق</span>
                </div>
              </div>
              <div className="property-revenue">
                <div className="revenue-header">
                  <span>نسبة العروض</span>
                  <span className="revenue-value">{offerPercent.toFixed(0)}%</span>
                </div>
                <div className="revenue-bar">
                  <div 
                    className="revenue-fill" 
                    style={{ width: `${offerPercent}%`, background: property.color }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Row */}
      <div className="bottom-row">
        {/* City Performance */}
        <div className="chart-card city-performance">
          <div className="chart-header">
            <div>
              <h3>التوزيع حسب المدينة</h3>
              <p className="chart-subtitle">العروض والطلبات حسب المدينة</p>
            </div>
          </div>
          <div className="city-list">
            {cityPerformance.map((city, index) => (
              <div key={index} className="city-item">
                <div className="city-rank">{index + 1}</div>
                <div className="city-info">
                  <div className="city-name">
                    <MapPin size={16} />
                    <span>{city.city}</span>
                  </div>
                  <div className="city-stats">
                    <span>{city.offers} عرض</span>
                    <span>{city.requests} طلب</span>
                  </div>
                </div>
                <div className="city-revenue">
                  <span className="revenue">{city.matches} تطابق</span>
                  <span className={`growth ${city.growth >= 0 ? 'positive' : 'negative'}`}>
                    {city.growth >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {Math.abs(city.growth)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Offers */}
        <div className="chart-card top-offers">
          <div className="chart-header">
            <div>
              <h3>عروضي</h3>
              <p className="chart-subtitle">قائمة العروض المضافة</p>
            </div>
          </div>
          <div className="top-offers-list">
            {topOffers.map((offer, index) => (
              <div key={offer.id} className="top-offer-item">
                <div className="offer-rank">
                  <span className={`rank-badge rank-${index + 1}`}>{index + 1}</span>
                </div>
                <div className="offer-details">
                  <h4>{offer.title}</h4>
                  <span className="offer-price">{formatPrice(offer.price)}</span>
                </div>
                <div className="offer-metrics">
                  <div className="metric-pill">
                    <MapPin size={14} />
                    {offer.city}
                  </div>
                  <div className={`metric-pill ${offer.status === 'available' ? 'conversion' : 'reserved'}`}>
                    {offer.status === 'available' ? 'متاح' : 'محجوز'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Matching Stats */}
      <div className="weekly-comparison">
        <div className="comparison-header">
          <h3>إحصائيات التطابق</h3>
          <span className="comparison-period">نظام المطابقة الذكي</span>
        </div>
        <div className="comparison-grid">
          <div className="comparison-item">
            <div className="comparison-icon views">
              <Sparkles size={20} />
            </div>
            <div className="comparison-data">
              <span className="current">{matchingStatsFormatted.excellent.count}</span>
            </div>
            <span className="comparison-label">{matchingStatsFormatted.excellent.label}</span>
            <span className="comparison-change up">90%+</span>
          </div>
          <div className="comparison-item">
            <div className="comparison-icon contacts">
              <Target size={20} />
            </div>
            <div className="comparison-data">
              <span className="current">{matchingStatsFormatted.good.count}</span>
            </div>
            <span className="comparison-label">{matchingStatsFormatted.good.label}</span>
            <span className="comparison-change up">75-89%</span>
          </div>
          <div className="comparison-item">
            <div className="comparison-icon deals">
              <CheckCircle size={20} />
            </div>
            <div className="comparison-data">
              <span className="current">{matchingStatsFormatted.fair.count}</span>
            </div>
            <span className="comparison-label">{matchingStatsFormatted.fair.label}</span>
            <span className="comparison-change">50-74%</span>
          </div>
        </div>
      </div>

      {/* Best Performing Offers Section */}
      <div className="section-header">
        <h2>
          <Trophy size={24} style={{ color: '#f59e0b' }} />
          أفضل العروض أداءً
        </h2>
        <p>العروض الأكثر وصولاً وتفاعلاً</p>
      </div>
      <div className="best-offers-grid">
        {bestPerformingOffers.map((offer, index) => {
          const engagementScore = (offer.views || 0) + (offer.shares || 0) * 5 + (offer.inquiries || 0) * 10;
          const maxEngagement = Math.max(...bestPerformingOffers.map(o => (o.views || 0) + (o.shares || 0) * 5 + (o.inquiries || 0) * 10), 1);
          const engagementPercent = maxEngagement > 0 ? (engagementScore / maxEngagement) * 100 : 0;
          
          return (
            <div key={offer.id} className={`best-offer-card ${index === 0 ? 'top-performer' : ''}`}>
              {index === 0 && (
                <div className="top-badge">
                  <Flame size={16} />
                  الأعلى أداءً
                </div>
              )}
              <div className="best-offer-header">
                <div className="offer-rank-large">
                  <span className={`rank-number rank-${index + 1}`}>#{index + 1}</span>
                </div>
                <div className="offer-main-info">
                  <h3>{offer.title}</h3>
                  <div className="offer-meta">
                    <span className="price-tag">{formatPrice(offer.price)}</span>
                    <span className="location-tag">
                      <MapPin size={14} />
                      {offer.city}
                    </span>
                    <span className={`status-tag ${offer.status}`}>
                      {offer.status === 'available' ? 'متاح' : 'محجوز'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="performance-metrics">
                <div className="perf-metric">
                  <div className="perf-icon views">
                    <Eye size={18} />
                  </div>
                  <div className="perf-data">
                    <span className="perf-value">{offer.views}</span>
                    <span className="perf-label">مشاهدة</span>
                  </div>
                </div>
                <div className="perf-metric">
                  <div className="perf-icon shares">
                    <Share2 size={18} />
                  </div>
                  <div className="perf-data">
                    <span className="perf-value">{offer.shares}</span>
                    <span className="perf-label">مشاركة</span>
                  </div>
                </div>
                <div className="perf-metric">
                  <div className="perf-icon inquiries">
                    <Phone size={18} />
                  </div>
                  <div className="perf-data">
                    <span className="perf-value">{offer.inquiries}</span>
                    <span className="perf-label">استفسار</span>
                  </div>
                </div>
                <div className="perf-metric">
                  <div className="perf-icon saves">
                    <Bookmark size={18} />
                  </div>
                  <div className="perf-data">
                    <span className="perf-value">{offer.saves}</span>
                    <span className="perf-label">حفظ</span>
                  </div>
                </div>
              </div>
              
              <div className="engagement-bar-section">
                <div className="engagement-header">
                  <span>معدل التفاعل</span>
                  <span className="engagement-score">{engagementPercent.toFixed(0)}%</span>
                </div>
                <div className="engagement-bar">
                  <div 
                    className="engagement-fill"
                    style={{ width: `${engagementPercent}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total Reach Summary */}
      <div className="reach-summary">
        <div className="reach-header">
          <h3>إجمالي الوصول</h3>
          <span className="reach-period">جميع العروض</span>
        </div>
        <div className="reach-stats">
          <div className="reach-stat">
            <div className="reach-icon">
              <Eye size={24} />
            </div>
            <div className="reach-data">
              <span className="reach-value">{topOffers.reduce((sum, o) => sum + o.views, 0).toLocaleString()}</span>
              <span className="reach-label">إجمالي المشاهدات</span>
            </div>
          </div>
          <div className="reach-stat">
            <div className="reach-icon">
              <Share2 size={24} />
            </div>
            <div className="reach-data">
              <span className="reach-value">{topOffers.reduce((sum, o) => sum + o.shares, 0)}</span>
              <span className="reach-label">إجمالي المشاركات</span>
            </div>
          </div>
          <div className="reach-stat">
            <div className="reach-icon">
              <Phone size={24} />
            </div>
            <div className="reach-data">
              <span className="reach-value">{topOffers.reduce((sum, o) => sum + o.inquiries, 0)}</span>
              <span className="reach-label">إجمالي الاستفسارات</span>
            </div>
          </div>
          <div className="reach-stat">
            <div className="reach-icon">
              <Bookmark size={24} />
            </div>
            <div className="reach-data">
              <span className="reach-value">{topOffers.reduce((sum, o) => sum + o.saves, 0)}</span>
              <span className="reach-label">إجمالي الحفظ</span>
            </div>
          </div>
        </div>
      </div>

      {/* Financial Statistics Section */}
      <div className="section-header financial-section-header">
        <h2>
          <Receipt size={24} style={{ color: '#10b981' }} />
          الإحصائيات المالية
        </h2>
        <p>ملخص الإيرادات والمصروفات</p>
      </div>

      {/* Financial KPIs */}
      <div className="financial-kpi-grid">
        <div className="financial-kpi-card income">
          <div className="financial-kpi-icon">
            <TrendingUp size={28} />
          </div>
          <div className="financial-kpi-content">
            <span className="financial-kpi-value">{formatPrice(financialStatsFormatted.totalIncome)}</span>
            <span className="financial-kpi-label">إجمالي الإيرادات</span>
          </div>
          <div className="financial-kpi-badge positive">
            <ArrowUpRight size={14} />
            {financialStatsFormatted.thisMonthIncome > 0 ? '+' : ''}{financialStatsFormatted.thisMonthIncome > 0 ? Math.round((financialStatsFormatted.thisMonthIncome / financialStatsFormatted.totalIncome) * 100) : 0}%
          </div>
        </div>

        <div className="financial-kpi-card expense">
          <div className="financial-kpi-icon">
            <TrendingDown size={28} />
          </div>
          <div className="financial-kpi-content">
            <span className="financial-kpi-value">{formatPrice(financialStatsFormatted.totalExpenses)}</span>
            <span className="financial-kpi-label">إجمالي المصروفات</span>
          </div>
          <div className="financial-kpi-badge negative">
            <ArrowDownRight size={14} />
            مدفوع
          </div>
        </div>

        <div className="financial-kpi-card profit">
          <div className="financial-kpi-icon">
            <PiggyBank size={28} />
          </div>
          <div className="financial-kpi-content">
            <span className="financial-kpi-value">{formatPrice(financialStatsFormatted.netProfit)}</span>
            <span className="financial-kpi-label">صافي الربح</span>
          </div>
          <div className="financial-kpi-badge positive">
            <ArrowUpRight size={14} />
            {financialStatsFormatted.totalIncome > 0 ? Math.round((financialStatsFormatted.netProfit / financialStatsFormatted.totalIncome) * 100) : 0}%
          </div>
        </div>

        <div className="financial-kpi-card pending">
          <div className="financial-kpi-icon">
            <Wallet size={28} />
          </div>
          <div className="financial-kpi-content">
            <span className="financial-kpi-value">{formatPrice(financialStatsFormatted.pendingIncome)}</span>
            <span className="financial-kpi-label">قيد التحصيل</span>
          </div>
          <div className="financial-kpi-badge warning">
            {financialStatsFormatted.pendingInvoices} فاتورة
          </div>
        </div>
      </div>

      {/* Financial Charts Row */}
      <div className="charts-row">
        <div className="chart-card main-chart">
          <div className="chart-header">
            <div>
              <h3>الإيرادات والمصروفات</h3>
              <p className="chart-subtitle">تتبع المالية شهرياً</p>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={financialData}>
                <defs>
                  <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                <XAxis dataKey="month" stroke="var(--text-secondary)" fontSize={12} />
                <YAxis stroke="var(--text-secondary)" fontSize={12} />
                <Tooltip 
                  contentStyle={{ 
                    background: 'var(--surface-color)', 
                    border: '1px solid var(--border-color)', 
                    borderRadius: '12px',
                    boxShadow: 'var(--shadow-lg)'
                  }}
                  formatter={(value, name) => {
                    if (name === 'income') return [formatPrice(value), 'الإيرادات'];
                    if (name === 'expenses') return [formatPrice(value), 'المصروفات'];
                    return [value, name];
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="income" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorIncome)" 
                />
                <Area 
                  type="monotone" 
                  dataKey="expenses" 
                  stroke="#ef4444" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorExpenses)" 
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="chart-card commission-summary">
          <div className="chart-header">
            <div>
              <h3>ملخص العمولات</h3>
              <p className="chart-subtitle">تفاصيل العمولات والصفقات</p>
            </div>
          </div>
          <div className="commission-stats">
            <div className="commission-stat">
              <div className="commission-icon">
                <DollarSign size={24} />
              </div>
              <div className="commission-info">
                <span className="commission-value">{formatPrice(financialStatsFormatted.commissions)}</span>
                <span className="commission-label">إجمالي العمولات</span>
              </div>
            </div>
            <div className="commission-breakdown">
              <div className="breakdown-item">
                <span className="breakdown-label">عمولات مدفوعة</span>
                <span className="breakdown-value paid">{formatPrice(financialStatsFormatted.totalIncome - financialStatsFormatted.pendingIncome)}</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">عمولات قيد التحصيل</span>
                <span className="breakdown-value pending">{formatPrice(financialStatsFormatted.pendingIncome)}</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">صافي الربح</span>
                <span className="breakdown-value">{formatPrice(financialStatsFormatted.netProfit)}</span>
              </div>
              <div className="breakdown-item">
                <span className="breakdown-label">إجمالي الفواتير</span>
                <span className="breakdown-value highlight">{financialStatsFormatted.paidInvoices + financialStatsFormatted.pendingInvoices}</span>
              </div>
            </div>
            <div className="commission-actions">
              <a href="/invoices" className="view-invoices-btn">
                <Receipt size={18} />
                عرض جميع الفواتير
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Status Summary */}
      <div className="invoice-status-summary">
        <div className="invoice-status-header">
          <h3>حالة الفواتير</h3>
          <span className="total-invoices">{financialStatsFormatted.paidInvoices + financialStatsFormatted.pendingInvoices} فاتورة</span>
        </div>
        <div className="invoice-status-grid">
          <div className="invoice-status-item paid">
            <div className="status-icon">
              <CheckCircle size={20} />
            </div>
            <div className="status-data">
              <span className="status-count">{financialStatsFormatted.paidInvoices}</span>
              <span className="status-label">مدفوعة</span>
            </div>
            <div className="status-bar">
              <div className="status-fill" style={{ width: `${(financialStatsFormatted.paidInvoices / (financialStatsFormatted.paidInvoices + financialStatsFormatted.pendingInvoices)) * 100}%` }}></div>
            </div>
          </div>
          <div className="invoice-status-item pending">
            <div className="status-icon">
              <AlertCircle size={20} />
            </div>
            <div className="status-data">
              <span className="status-count">{financialStatsFormatted.pendingInvoices}</span>
              <span className="status-label">قيد الانتظار</span>
            </div>
            <div className="status-bar">
              <div className="status-fill" style={{ width: `${(financialStatsFormatted.pendingInvoices / (financialStatsFormatted.paidInvoices + financialStatsFormatted.pendingInvoices)) * 100}%` }}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </PermissionGate>
  );
};

export default StatisticsPage;
